// src/hooks/useShopSettings.js
// 把 ShopSettingsPage.jsx 里"settings从哪来、怎么改、怎么存"这一整套状态管理逻辑
// 抽出来——纯粹的重构，不改变任何行为，只是把逻辑和UI排版分开，方便以后维护
import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { shopSettingsApi, uploadApi } from '../api';

export function useShopSettings() {
  const { setShopSettings, language } = useApp();

  const [settings, setSettings] = useState({
    shop_name: '', shop_name_en: '', shop_name_km: '', shop_name_ms: '', shop_name_zh_tw: '',
    shop_tag: '', shop_tag_en: '', shop_tag_km: '', shop_tag_ms: '', shop_tag_zh_tw: '',
    logo_url: '', primary_color: '#F97316',
    phone: '', email: '', address: '', facebook: '', telegram: '',
    enable_pickup: 1, enable_delivery: 1,
    enable_khqr: 1, enable_alipay: 1, enable_usdt: 1, enable_cod: 1,
    khr_rate: 4200,
    free_shipping_threshold: '', base_delivery_fee: '',
    business_hours_start: '', business_hours_end: '',
    enable_instant_delivery: 1,
    free_shipping_for_scheduled: 0,
    stock_unlimited: 0,
    full_reduction_enabled: 0,
    full_reduction_vip_allowed: 1,
    distance_fee_enabled: 0,
    delivery_fee_store_source: 'auto',
    default_delivery_store_id: '',
    delivery_fee_collected_externally: 0,
    external_fee_amount_condition_enabled: 0,
    external_fee_min_amount: '',
    external_fee_vip_condition_enabled: 0,
    external_fee_member_condition: '',
    external_fee_item_count_condition_enabled: 0,
    external_fee_min_item_count: '',
    branch_transfer_requires_approval: 0,
    customer_service_app_key: '',
    customer_service_app_secret: '',
    sso_partner_key: '',
    sso_partner_secret: '',
    pos_enable_cash: true,
    pos_enable_card: true,
    pos_enable_mobile: true,
    pos_enable_credit: false,
    enabled_languages: ['zh', 'en', 'km'],
  });

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await shopSettingsApi.getSettings();
      if (res.success && res.data) {
        const s = res.data;
        setSettings({
          shop_name:       s.shop_name      || '',
          shop_name_en:    s.shop_name_en   || '',
          shop_name_km:    s.shop_name_km   || '',
          shop_name_ms:    s.shop_name_ms   || '',
          shop_name_zh_tw: s.shop_name_zh_tw || '',
          shop_tag:        s.shop_tag       || '',
          shop_tag_en:     s.shop_tag_en    || '',
          shop_tag_km:     s.shop_tag_km    || '',
          shop_tag_ms:     s.shop_tag_ms    || '',
          shop_tag_zh_tw:  s.shop_tag_zh_tw || '',
          logo_url:        s.logo_url       || '',
          primary_color:   s.primary_color  || '#F97316',
          phone:           s.phone          || '',
          email:           s.email          || '',
          address:         s.address        || '',
          facebook:        s.facebook       || '',
          telegram:        s.telegram       || '',
          enable_pickup:   s.enable_pickup   !== undefined ? s.enable_pickup   : 1,
          enable_delivery: s.enable_delivery !== undefined ? s.enable_delivery : 1,
          enable_khqr:     s.enable_khqr     !== undefined ? s.enable_khqr     : 1,
          enable_alipay:   s.enable_alipay   !== undefined ? s.enable_alipay   : 1,
          enable_usdt:     s.enable_usdt     !== undefined ? s.enable_usdt     : 1,
          enable_cod:      s.enable_cod      !== undefined ? s.enable_cod      : 1,
          khr_rate:        s.khr_rate       || 4200,
          free_shipping_threshold: s.free_shipping_threshold ?? '',
          base_delivery_fee:       s.base_delivery_fee ?? '',
          business_hours_start:    s.business_hours_start || '',
          business_hours_end:      s.business_hours_end   || '',
          enable_instant_delivery: s.enable_instant_delivery !== undefined ? s.enable_instant_delivery : 1,
          free_shipping_for_scheduled: s.free_shipping_for_scheduled !== undefined ? s.free_shipping_for_scheduled : 0,
          stock_unlimited: s.stock_unlimited !== undefined ? s.stock_unlimited : 0,
          full_reduction_enabled: s.full_reduction_enabled !== undefined ? s.full_reduction_enabled : 0,
          full_reduction_vip_allowed: s.full_reduction_vip_allowed !== undefined ? s.full_reduction_vip_allowed : 1,
          distance_fee_enabled: s.distance_fee_enabled !== undefined ? s.distance_fee_enabled : 0,
          delivery_fee_store_source: s.delivery_fee_store_source || 'auto',
          default_delivery_store_id: s.default_delivery_store_id || '',
          delivery_fee_collected_externally: s.delivery_fee_collected_externally !== undefined ? s.delivery_fee_collected_externally : 0,
          external_fee_amount_condition_enabled: s.external_fee_amount_condition_enabled !== undefined ? s.external_fee_amount_condition_enabled : 0,
          external_fee_min_amount: s.external_fee_min_amount ?? '',
          external_fee_vip_condition_enabled: s.external_fee_vip_condition_enabled !== undefined ? s.external_fee_vip_condition_enabled : 0,
          external_fee_member_condition: s.external_fee_member_condition || '',
          external_fee_item_count_condition_enabled: s.external_fee_item_count_condition_enabled !== undefined ? s.external_fee_item_count_condition_enabled : 0,
          external_fee_min_item_count: s.external_fee_min_item_count ?? '',
          branch_transfer_requires_approval: s.branch_transfer_requires_approval !== undefined ? s.branch_transfer_requires_approval : 0,
          customer_service_app_key: s.customer_service_app_key || '',
          customer_service_app_secret: s.customer_service_app_secret || '',
          sso_partner_key: s.sso_partner_key || '',
          sso_partner_secret: s.sso_partner_secret || '',
          pos_enable_cash: s.pos_enable_cash !== 0 && s.pos_enable_cash !== false,
          pos_enable_card: s.pos_enable_card !== 0 && s.pos_enable_card !== false,
          pos_enable_mobile: s.pos_enable_mobile !== 0 && s.pos_enable_mobile !== false,
          pos_enable_credit: s.pos_enable_credit === 1 || s.pos_enable_credit === true,
          enabled_languages: Array.isArray(s.enabled_languages)
            ? s.enabled_languages
            : (typeof s.enabled_languages === 'string' ? s.enabled_languages.split(',').filter(Boolean) : ['zh', 'en', 'km']),
        });
        if (s.logo_url) setLogoPreview(s.logo_url);
        setBanners(s.banners || []);
      }
    } catch (err) {
      setError('加载设置失败');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const toggleField = (field) => {
    setSettings(prev => ({ ...prev, [field]: prev[field] ? 0 : 1 }));
  };

  // 语言开关：H5和POS已经合并成一个，至少要保留一种语言
  const toggleLanguage = (code) => {
    setSettings(prev => {
      const list = prev.enabled_languages;
      const has = list.includes(code);
      if (has && list.length === 1) {
        alert('至少要保留一种语言');
        return prev;
      }
      const next = has ? list.filter(c => c !== code) : [...list, code];
      return { ...prev, enabled_languages: next };
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('图片不能超过 5MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile) return settings.logo_url;
    setUploadingLogo(true);
    try {
      const res = await uploadApi.uploadProductImage(logoFile);
      if (res.success && res.data?.url) return res.data.url;
      throw new Error(res.message || '上传失败');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!settings.shop_name) { setError('店铺名称不能为空'); return; }
    if (!settings.khr_rate || settings.khr_rate < 100) { setError('KHR 汇率不能小于 100'); return; }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let logoUrl = settings.logo_url;
      if (logoFile) logoUrl = await uploadLogo();

      const res = await shopSettingsApi.updateSettings({ ...settings, logo_url: logoUrl });
      if (res.success) {
        setSuccess('保存成功！');
        setLogoFile(null);
        setSettings(prev => ({ ...prev, logo_url: logoUrl }));
        if (setShopSettings) setShopSettings(res.data);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(res.message || '保存失败');
      }
    } catch (err) {
      setError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return {
    language,
    settings, updateField, toggleField, toggleLanguage,
    banners, setBanners,
    loading, saving, error, setError, success,
    logoPreview, uploadingLogo, handleLogoChange,
    handleSave,
  };
}